/*
  Product lightbox.

  Dawn's stock behaviour puts every piece of a product's media into the modal
  at once and relies on scrolling: `showActiveMedia` marks the clicked item
  `.active`, calls `scrollIntoView()`, and above 750px the CSS reveals all the
  other items too, so the modal is one long scrollable column.

  The store's previous gallery app replaced that with a paged, one-image-at-a-time
  lightbox. That behaviour now lives here instead of in an app: exactly one item
  is shown, and prev/next buttons plus the left/right arrow keys page between
  them, wrapping at both ends so neither button is ever dead.

  Paired with the CSS in custom-product-gallery.css, which hides the inactive
  items at every breakpoint and bounds the visible one to the viewport.
*/
if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.activeIndex = 0;
      }

      get content() {
        return this.querySelector('[role="document"]');
      }

      /*
        The set of media the lightbox pages through, which must match what the
        gallery shows.

        Direct children only: each piece of media is a direct child of the
        content element — an <img> for images, <deferred-media> for video, or a
        .product-media-modal__model wrapper for 3D models. Querying the whole
        subtree would also pick up the poster <img> inside a video's play
        button, which is not a gallery item.

        Variant images are then dropped. This matters because the gallery and
        the modal disagree about how to exclude them: the gallery skips them in
        Liquid (product-media-gallery.liquid:88), so they are never in its DOM,
        while the modal renders every piece of media and hides them in CSS
        (section-main-product.css:433) — keeping only :first-child, which is the
        selected variant's own image.

        That CSS rule doubles its class for a specificity of (0,3,0), which
        beats this file's :not(.active) rule, so a variant image stays hidden
        even when marked active. Paging onto one would show an empty lightbox.
      */
      get mediaItems() {
        if (!this.content) return [];
        return Array.from(this.content.children).filter(
          (element, index) =>
            element.hasAttribute('data-media-id') &&
            (index === 0 || !element.classList.contains('product__media-item--variant'))
        );
      }

      /*
        Deferred until the first open: the element upgrades before its markup is
        necessarily parsed, so the buttons may not exist in the constructor.
      */
      setup() {
        if (this.hasSetup) return;
        this.hasSetup = true;

        this.prevButton = this.querySelector('[data-modal-prev]');
        this.nextButton = this.querySelector('[data-modal-next]');
        this.counter = this.querySelector('[data-modal-counter-current]');
        this.counterTotal = this.querySelector('[data-modal-counter-total]');

        if (this.prevButton) this.prevButton.addEventListener('click', () => this.step(-1));
        if (this.nextButton) this.nextButton.addEventListener('click', () => this.step(1));

        /*
          ModalDialog closes a `.media-modal` on any mouse pointerup that is not
          inside deferred-media or product-model (global.js:609) — that is how
          click-anywhere-to-close works. The paging controls are inside neither,
          so without this every click on an arrow would close the lightbox
          instead of paging it.

          Stopping the event at the controls is preferable to widening that
          selector in global.js, which is shared by every modal in the theme.
          pointerup is stopped, not click, so the button's own click handler
          above still fires.
        */
        const nav = this.querySelector('.product-media-modal__nav');
        if (nav) nav.addEventListener('pointerup', (event) => event.stopPropagation());

        this.addEventListener('keydown', (event) => {
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          event.preventDefault();
          this.step(event.key === 'ArrowLeft' ? -1 : 1);
        });
      }

      hide() {
        this.pauseActiveMedia();
        super.hide();
      }

      show(opener) {
        super.show(opener);
        this.setup();
        this.showActiveMedia();
      }

      showActiveMedia() {
        const openedById = this.openedBy ? this.openedBy.getAttribute('data-media-id') : null;
        const index = this.mediaItems.findIndex((element) => element.getAttribute('data-media-id') === openedById);
        this.setActiveMedia(index === -1 ? 0 : index);
      }

      step(direction) {
        const total = this.mediaItems.length;
        if (total < 2) return;
        // Wrap, so neither arrow is ever a dead control.
        this.setActiveMedia((this.activeIndex + direction + total) % total);
      }

      /*
        Stops a playing <video>/<audio> when it is navigated away from or the
        modal closes. Without this the file keeps playing behind display: none.
        Note this cannot reach an external YouTube/Vimeo iframe, which would
        need their postMessage APIs; the store's media is currently all images.
      */
      pauseActiveMedia() {
        const active = this.mediaItems[this.activeIndex];
        if (!active) return;
        active.querySelectorAll('video, audio').forEach((element) => {
          if (typeof element.pause === 'function') element.pause();
        });
      }

      setActiveMedia(index) {
        const items = this.mediaItems;
        if (!items.length) return;

        if (index !== this.activeIndex) this.pauseActiveMedia();
        this.activeIndex = index;

        items.forEach((element, i) => element.classList.toggle('active', i === index));

        if (this.counter) this.counter.textContent = index + 1;

        /*
          Total is taken from the live DOM rather than trusted from Liquid.
          product-info.js:309 replaces the modal content's innerHTML on every
          variant change, so the number of items can shift after first render.
        */
        if (this.counterTotal) this.counterTotal.textContent = items.length;

        /*
          Only one item is visible now, so nothing should be scrolled. Reset in
          case an earlier interaction left the container offset — Dawn's
          original code deliberately set scrollLeft to centre a wide image.
        */
        if (this.content) {
          this.content.scrollTop = 0;
          this.content.scrollLeft = 0;
        }

        const activeMedia = items[index];
        const template = activeMedia.querySelector('template');
        const templateContent = template ? template.content : null;

        if (
          activeMedia.nodeName === 'DEFERRED-MEDIA' &&
          templateContent &&
          templateContent.querySelector('.js-youtube')
        ) {
          activeMedia.loadContent();
        }
      }
    }
  );
}
